/**
 * The Share surface's action definitions (FN-8) — ONE list, two layouts.
 *
 * Sharing used to be spread across three places: the Share menu (access), the
 * Publish menu (preview links, under a "Share a draft" heading), and the
 * account menu (saved previews). Three doors onto "let someone else see this",
 * none of which said what the person on the other end could actually do.
 *
 * Every hint here is written from the RECIPIENT's side, because that is the
 * question a share surface answers. "Create a view-only link" describes what
 * you do; "Anyone with the link can view this version" describes what they get,
 * which is what determines whether it is the right link to send.
 *
 * WHAT THIS MODULE DELIBERATELY DOES NOT HOLD: the `data-*-action` attribute
 * literals. `scripts/tests/command-bar-census.js` harvests those from a fixed
 * list of authoring files to build the action-parity matrix, and this file is
 * not one of them — moving the literals here would empty the matrix while every
 * test still passed. The attributes stay authored at their call site in
 * `destination-region.js`; this module owns labels, hints, glyphs, events and
 * capability requirements, which is the half that was actually duplicated.
 */

/**
 * `requires` is the capability the action needs, checked by whichever surface
 * renders it:
 *   'cloud'  — a project saved to the account (state.cloudVersion)
 *   'collab' — collaboration switched on for this project
 */
export const SHARE_ACTIONS = Object.freeze([
  Object.freeze({
    id: 'invite',
    label: 'Invite people to edit…',
    hint: 'They sign in and edit this project with you.',
    glyph: 'share',
    event: 'toolbar:share-project',
    payload: { access: 'edit' },
    requires: 'cloud'
  }),
  Object.freeze({
    id: 'review-link',
    label: 'Get a review link…',
    hint: 'Anyone with the link can view and leave comments.',
    glyph: 'eye',
    event: 'toolbar:share-project',
    payload: { access: 'review' },
    requires: 'cloud'
  }),
  Object.freeze({
    id: 'save-preview',
    label: 'Save a preview link…',
    hint: 'Anyone with the link can view this version. No commenting or editing.',
    glyph: 'previews',
    event: 'toolbar:save-preview',
    requires: 'cloud'
  }),
  Object.freeze({
    id: 'my-previews',
    label: 'My previews…',
    hint: "Open or manage the preview links you've saved.",
    glyph: 'previews',
    event: 'toolbar:my-previews',
    requires: null
  }),
  Object.freeze({
    id: 'comments',
    label: 'Comments…',
    hint: 'Read and reply to comments on this project.',
    // No glyph, deliberately: the shell rewrites this row's label to carry an
    // unseen count, and an icon child would have to survive that rewrite.
    glyph: null,
    event: 'toolbar:open-comments',
    requires: 'collab'
  })
]);

export function shareActionById(id) {
  return SHARE_ACTIONS.find((a) => a.id === id) || null;
}
